import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

// QR Code Component for Certificate
const QRCodeComponent = ({ url, size = 64 }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const qrCodeURL = await QRCode.toDataURL(url, {
          width: size,
          margin: 1,
          color: {
            dark: '#000000',  // Black QR code
            light: '#FFFFFF'  // White background
          }
        });
        setQrCodeDataURL(qrCodeURL);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [url, size]);

  return qrCodeDataURL ? (
    <img 
      src={qrCodeDataURL} 
      alt={`QR Code für ${url}`}
      className="mx-auto rounded"
      style={{ width: size, height: size }}
    />
  ) : (
    <div className="w-16 h-16 mx-auto bg-gray-300 rounded flex items-center justify-center">
      <span className="text-xs text-gray-600">Laden...</span>
    </div>
  );
};

// Enhanced Modern Certificate Component with Unfold Sales Branding
const ModernCertificate = ({ user, completedCategories, certificateId, issuedDate }) => {
  // Determine certificate type based on user role
  const isTeamleiter = user?.role === 'teamleiter' || user?.role === 'vertriebsleiter';
  const certificateType = isTeamleiter ? 'Teamleiter' : 'Vertriebler';
  const certificatePrefix = isTeamleiter ? 'TL' : 'VT';
  
  return (
    <div className="certificate-print certificate-background bg-white w-full max-w-4xl mx-auto p-12 border-2 border-gray-200 rounded-3xl shadow-2xl relative overflow-hidden certificate-container">
      {/* Background Pattern - Bottom Left */}
      <div className="absolute bottom-0 left-0 w-56 h-56 opacity-3">
        <div className="w-full h-full bg-gradient-to-tr from-[#5C5C5C] to-[#0F111A] rounded-full transform -translate-x-12 translate-y-12"></div>
      </div>
      
      {/* Role Badge - Top Right */}
      <div className="absolute top-8 right-8 z-20 bg-blue-600 px-6 py-2 rounded-full shadow-lg">
        <span className="text-sm font-bold text-white uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {certificateType}
        </span>
      </div>
      
      {/* Header Section with Dark Background for entire top area */}
      <div className="flex items-center justify-center mb-12 relative z-10 bg-[#0F111A] rounded-t-3xl py-6 px-12 -mx-12 -mt-12">
        <img 
          src="https://customer-assets.emergentagent.com/job_sales-academy-hub/artifacts/gntwx7dt_Logo%20Schriftzug%20us%20remove%20bg.png"
          alt="Unfold Sales Logo"
          className="h-32 object-contain"
        />
      </div>
      
      {/* Main Content with Enhanced Typography */}
      <div className="text-center space-y-8 relative z-10">
        {/* Elegant Certifies Text */}
        <div>
          <h2 className="text-2xl font-light text-[#5C5C5C] mb-3" style={{ fontFamily: 'Lora, serif' }}>
            Hiermit wird bestätigt, dass
          </h2>
        </div>
        
        {/* Enhanced User Name */}
        <div className="py-8 px-12 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl border-2 border-gray-200 shadow-sm">
          <h1 className="certificate-title text-6xl font-black text-[#0F111A] leading-tight tracking-wide" 
              style={{ fontFamily: 'Poppins, sans-serif', textShadow: '0 2px 4px rgba(15, 17, 26, 0.1)' }}>
            {user?.name || 'Teilnehmer Name'}
          </h1>
        </div>
        
        {/* Refined Achievement Text */}
        <div className="space-y-5">
          <p className="text-xl text-[#5C5C5C] leading-relaxed font-light" style={{ fontFamily: 'Lora, serif' }}>
            erfolgreich die {certificateType} Schulung der
          </p>
          <h3 className="text-4xl font-bold text-[#0F111A] mb-4" 
              style={{ fontFamily: 'Poppins, sans-serif' }}>
            unfold sales Akademie
          </h3>
          <p className="text-lg text-[#777777] font-light" style={{ fontFamily: 'Lora, serif' }}>
            erfolgreich abgeschlossen hat.
          </p>
        </div>
        
        {/* Enhanced Categories Display */}
        {completedCategories && completedCategories.length > 0 && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h4 className="text-lg font-semibold text-[#0F111A] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Abgeschlossene Schulungsbereiche:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {completedCategories.map((category, index) => (
                <div key={index} className="bg-white px-5 py-3 rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-sm font-medium text-[#1C1C1C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {category}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Enhanced Footer with proper contrast */}
      <div className="mt-16 pt-8 border-t-2 border-gray-200 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {/* Issue Date - Light background with dark text */}
          <div className="bg-[#F4F4F4] rounded-xl p-4">
            <h5 className="text-xs font-bold text-[#0F111A] uppercase tracking-wider mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Ausstellungsdatum
            </h5>
            <p className="text-base font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Lora, serif' }}>
              {issuedDate ? new Date(issuedDate).toLocaleDateString('de-DE', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric'
              }) : new Date().toLocaleDateString('de-DE', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric'
              })}
            </p>
          </div>
          
          {/* QR Code - Modern elegant design with dark background */}
          <div className="bg-[#0F111A] rounded-xl p-6 shadow-lg border border-gray-700">
            <h5 className="text-xs font-bold text-white uppercase tracking-wider mb-4 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Zur Website
            </h5>
            <div className="flex justify-center mb-3">
              <div className="bg-white rounded-lg p-2 shadow-inner">
                <QRCodeComponent url="https://unfoldsales.de" size={64} />
              </div>
            </div>
            <p className="text-xs text-gray-300 text-center leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
              unfoldsales.de
            </p>
          </div>
          
          {/* Digital Signature - Light background with dark text */}
          <div className="bg-[#F4F4F4] rounded-xl p-4">
            <h5 className="text-xs font-bold text-[#0F111A] uppercase tracking-wider mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Digitale Signatur
            </h5>
            {/* Real Handwritten Signature - Force image display */}
            <div className="h-12 mb-2 flex items-center justify-center">
              <img 
                src="/marcel-signature.png?v=20251007v2"
                alt="Marcel Paulus Signature"
                className="h-10 object-contain"
                style={{ 
                  maxWidth: '140px',
                  imageRendering: 'crisp-edges',
                  display: 'block !important' // Force display
                }}
                onError={(e) => {
                  console.error('Signature image failed to load:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div className="border-t border-gray-300 pt-2">
              <p className="text-xs text-[#1C1C1C] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Marcel Paulus
              </p>
              <p className="text-xs text-[#5C5C5C]">
                Vertriebsleiter – unfold sales
              </p>
            </div>
          </div>
        </div>
        
        {/* Certificate Number */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400 font-mono">
            Zertifikatsnummer: {certificatePrefix}-{new Date().getFullYear()}-{certificateId ? certificateId.slice(0, 8).toUpperCase() : 'XXXXXXXX'}
          </p>
        </div>
      </div>
      
    </div>
  );
};

export default ModernCertificate;
